import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import PropertyCard from '../components/PropertyCard';
import { savedApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const SavedPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      fetchSaved();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchSaved = async () => {
    try {
      const response = await savedApi.getAll();
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching saved:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (propertyId) => {
    try {
      await savedApi.toggle(propertyId);
      setProperties(prev => prev.filter(p => p.property_id !== propertyId));
      toast.success('Removed from saved');
    } catch (error) {
      toast.error('Failed to unsave');
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="h-96 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="saved-page">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8">
          Saved Properties
        </h1>

        {properties.length === 0 ? (
          <div className="bg-soft-gray rounded-2xl p-12 text-center">
            <Heart className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              No saved properties
            </h3>
            <p className="text-slate-600 mb-6">
              Save properties you like to view them later.
            </p>
            <Link to="/browse">
              <Button className="bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full">
                Browse Listings
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {properties.map((property) => (
              <PropertyCard
                key={property.property_id}
                property={property}
                onSave={handleUnsave}
                isSaved={true}
                showOwner
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SavedPage;
